import { auth } from "@/api/firebase/firebase.config";
import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { useValidatorEmailOrUsername } from "@/features/auth/helperFunctions/validateEmailOrUsername";
import { Button, Flex, TextField, Text } from "@radix-ui/themes";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import useCloudFunctionCall from "@/hooks/useCloudFunctionCall";
import {
  AuthenticationProps,
  AuthenticationResponse,
} from "@/features/auth/types/authenticationTypes";
import { VerifyEmailCodeForm } from "./VerifyEmailCodeForm";
import { getPasswordRules } from "@/features/auth/helperFunctions/getPasswordRules";
import PasswordRule from "@/features/auth/components/PasswordRule/PasswordRule";

export const VerifiyEmail: FC<{}> = ({}) => {
  const navigate = useNavigate();
  const [verificationCodeSent, setVerificatonCodeSent] = useState<boolean>();
  const [response, _loading, _error, authenticate] =
    useCloudFunctionCall<AuthenticationResponse>(
      "userAuthentication",
      undefined
    );

  const [emailAuthValues, setEmailAuthValues] = useState<{
    emailVal: string;
    passwordVal: string;
    passwordVerifVal: string;
  }>({
    emailVal: auth.currentUser?.email || "",
    passwordVal: "",
    passwordVerifVal: "",
  });

  const baseAuthenticationProps = {
    method: "email",
    emailAddress: emailAuthValues.emailVal,
    platform: brand.brandValue,
    tenantId: brand.tenantId,
  } as AuthenticationProps;

  const [errors, setErrors] = useState<any>();

  // Password validation checks - matching Signup.tsx logic
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    emailAuthValues.emailVal
  );
  const isPasswordValid =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=!]).{8,}$/.test(
      emailAuthValues.passwordVal
    );
  const passwordsMatch =
    emailAuthValues.passwordVal === emailAuthValues.passwordVerifVal;

  // Get password rules display data
  const passwordRules = getPasswordRules({
    password: emailAuthValues.passwordVal,
    verifyPassword: emailAuthValues.passwordVerifVal,
  });

  const setFieldValue = async (targetName: string, value: any) => {
    return setEmailAuthValues((prev: any) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  useEffect(() => {
    if (auth.currentUser?.email) {
      handleUpdateEmail();
    }
  }, []);

  useValidatorEmailOrUsername("email", emailAuthValues.emailVal, setErrors);

  const handleUpdateEmail = async () => {
    await authenticate({
      ...baseAuthenticationProps,
      action: "convert-to-permanent",
      emailAddress: emailAuthValues.emailVal,
    }).catch(() => {
      return toast.error("Email is not available, please choose another.");
    });
  };

  useEffect(() => {
    if (!response) return;

    if (response.status === "verification-code-resent") {
      setVerificatonCodeSent(true);
      toast.info(`Code has been sent to ${emailAuthValues.emailVal}`);
    }
  }, [response]);

  if (verificationCodeSent) {
    return <VerifyEmailCodeForm emailAuthValues={emailAuthValues} />;
  }

  return (
    <form>
      <Flex direction="column" gap="4">
        {/* Email */}
        <Flex direction="column" gap="1">
          <Text as="label" size="2">
            E-mail address
          </Text>
          <TextField.Root
            placeholder="Email address..."
            size="3"
            type="text"
            value={emailAuthValues.emailVal}
            onChange={(e) => {
              setFieldValue("emailVal", e.target.value);
            }}
          />
        </Flex>

        {/* PASSWORD */}
        {emailAuthValues.emailVal && isEmailValid && (
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                Password
              </Text>
            </Flex>
            <TextField.Root
              placeholder="Enter a password"
              size="3"
              type="password"
              value={emailAuthValues.passwordVal}
              onChange={(e) => {
                setFieldValue("passwordVal", e.target.value);
              }}
            />
          </Flex>
        )}

        {/* PASSWORD VERIFY */}
        {emailAuthValues.emailVal && isEmailValid && (
          <Flex direction="column" gap="1">
            <Flex justify="between" align="end">
              <Text as="label" size="2">
                Repeat password
              </Text>
            </Flex>
            <TextField.Root
              placeholder="Repeat the password"
              size="3"
              type="password"
              value={emailAuthValues.passwordVerifVal}
              onChange={(e) => {
                setFieldValue("passwordVerifVal", e.target.value);
              }}
            />
          </Flex>
        )}

        {/* Password rules */}
        {emailAuthValues.emailVal && isEmailValid && (
          <Flex wrap="wrap" gapY="2">
            {passwordRules.map((passRule, index) => (
              <PasswordRule
                key={`passRule_${index}`}
                rule={passRule.rule}
                isValid={passRule.isValid}
              />
            ))}
          </Flex>
        )}

        <Button
          variant="solid"
          type="button"
          onClick={() => {
            handleUpdateEmail();
          }}
          disabled={!isEmailValid || !isPasswordValid || !passwordsMatch}
        >
          Verify account
        </Button>
      </Flex>
    </form>
  );
};
